// @ts-nocheck
import { supabase } from "@/integrations/supabase/client";

/**
 * Shared helper for ALL Supabase edge-function calls.
 *
 * Our edge functions return a JSON body of the shape
 *   { ok: false, error: { code, message } }
 * with the real reason a request failed. supabase-js, however, only surfaces
 * the generic string "Edge Function returned a non-2xx status code" on
 * `error.message`. This helper reads the actual response body (available on
 * `error.context` as a `Response`) and extracts the real message so users see
 * a meaningful error instead of the generic one.
 *
 * Drop-in replacement for `supabase.functions.invoke(name, options)` — it
 * returns the same `{ data, error }` shape, but `error.message` (when present)
 * is the real backend message.
 */

const GENERIC_FUNCTION_ERROR = "Edge Function returned a non-2xx status code";
export const GENERIC_FALLBACK_MESSAGE = "Something went wrong — please try again";

/**
 * Extract the real, human-readable message from a supabase-js functions error.
 * Reads the JSON body off `error.context` (a `Response`) when available.
 */
export async function getEdgeFunctionErrorMessage(
  error: any,
  fallback: string = GENERIC_FALLBACK_MESSAGE,
): Promise<string> {
  if (!error) return fallback;

  // Try to read the real error body off the Response attached to the error.
  try {
    const ctx = error.context;
    if (ctx && typeof ctx.json === "function") {
      // Clone so the body can still be read elsewhere if needed.
      const res = typeof ctx.clone === "function" ? ctx.clone() : ctx;
      const body = await res.json().catch(() => null);
      const msg =
        body?.error?.message ||
        body?.error ||
        body?.message;
      if (typeof msg === "string" && msg.trim()) return msg.trim();
    }
  } catch {
    // ignore — fall through to message-based extraction
  }

  // Fall back to error.message, unless it is the useless generic string.
  if (
    typeof error.message === "string" &&
    error.message.trim() &&
    error.message.trim() !== GENERIC_FUNCTION_ERROR
  ) {
    return error.message.trim();
  }

  return fallback;
}

interface InvokeOptions {
  body?: any;
  headers?: Record<string, string>;
  method?: string;
}

/**
 * Invoke an edge function. Mirrors `supabase.functions.invoke`'s return shape
 * (`{ data, error }`) but normalizes `error.message` to the real backend
 * message so the generic Supabase string never reaches a toast or the UI.
 *
 * Accepts either the supabase-style options object (`{ body, headers }`) or a
 * raw body as the second argument.
 */
export async function invokeEdgeFunction(
  name: string,
  optionsOrBody?: InvokeOptions | any,
): Promise<{ data: any; error: Error | null }> {
  const options: InvokeOptions =
    optionsOrBody &&
    typeof optionsOrBody === "object" &&
    ("body" in optionsOrBody ||
      "headers" in optionsOrBody ||
      "method" in optionsOrBody)
      ? optionsOrBody
      : { body: optionsOrBody };

  const { data, error } = await supabase.functions.invoke(name, options);

  if (error) {
    const message = await getEdgeFunctionErrorMessage(error);
    const normalized = new Error(message);
    // Preserve useful metadata for callers that need it.
    (normalized as any).context = (error as any).context;
    (normalized as any).originalError = error;
    // Keep the backend's error code (e.g. NOT_PUBLISHED) so callers can branch on it.
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.clone === "function") {
        const body = await ctx.clone().json().catch(() => null);
        if (body?.error?.code) (normalized as any).code = body.error.code;
      }
    } catch { /* ignore */ }
    return { data, error: normalized };
  }

  return { data, error: null };
}
